import Layout from '@/layouts/Layout';
import { useChatStream } from '@/hooks/useChatStream';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChatMessage } from '@domain/chat';
import { logError } from '@/lib/logger';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ChatAvailabilityBanner } from '@/components/chat/ChatAvailabilityBanner';
import { Button } from '@/components/ui/button';
import { Link } from '@inertiajs/react';
import { Card } from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';
import { ShieldAlert } from 'lucide-react';

export default function Show({
  chat,
  limitStatus,
}: {
  chat: {
    id: number;
    title?: string | null;
    messages: ChatMessage[];
    private?: boolean;
    expires_at?: string | null;
    expired?: boolean;
    expires_today?: boolean;
    risk_blocked?: boolean;
    risk_blocked_at?: string | null;
    content_hidden?: boolean;
  };
  limitStatus: {
    remaining: number;
    warningThreshold: number;
  };
}) {
  const [title, setTitle] = useState<string>(chat.title ?? `Chat #${chat.id}`);
  const [isPrivate, setIsPrivate] = useState<boolean>(chat.private !== false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [privacyError, setPrivacyError] = useState<string | null>(null);

  const [remainingMessages, setRemainingMessages] = useState(limitStatus.remaining);

  const decreaseCount = useCallback(() => {
    setRemainingMessages((prev) => Math.max(prev - 1, 0));
  }, [setRemainingMessages]);

  useEffect(() => {
    setRemainingMessages(limitStatus.remaining);
  }, [limitStatus.remaining]);

  const isChatBlocked = remainingMessages <= 0;
  const isChatExpired = Boolean(chat.expired);
  const expiresToday = Boolean(chat.expires_today);
  const isSafetyBlocked = Boolean(chat.risk_blocked);

  const chatId = chat.id;
  const fetchChatDetails = useCallback(async () => {
    try {
      const res = await fetch(`/patients/chats/${chatId}.json`, { credentials: 'include' });
      if (!res.ok) {
        return null;
      }
      const data = await res.json();
      return {
        title: typeof data?.title === 'string' ? (data.title as string) : null,
        private: typeof data?.private === 'boolean' ? (data.private as boolean) : null,
      };
    } catch (err) {
      logError('Impossibile aggiornare i metadati della chat', err);
      return null;
    }
  }, [chatId]);

  const handlePrivacyToggle = useCallback(
    async (nextPrivate: boolean) => {
      if (savingPrivacy || nextPrivate === isPrivate) {
        return;
      }

      const previousValue = isPrivate;
      setIsPrivate(nextPrivate);
      setSavingPrivacy(true);
      setPrivacyError(null);

      try {
        const csrfToken =
          document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
        const response = await fetch(`/patients/chats/${chatId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          credentials: 'include',
          body: JSON.stringify({ chat: { private: nextPrivate } }),
        });

        if (!response.ok) {
          throw new Error(`privacy_update_failed_${response.status}`);
        }

        const data = await response.json();
        if (typeof data?.private === 'boolean') {
          setIsPrivate(data.private as boolean);
        }
      } catch (err) {
        setIsPrivate(previousValue);
        setPrivacyError('Impossibile aggiornare la privacy della chat. Riprova più tardi.');
        logError('Errore durante il salvataggio della privacy chat', err);
      } finally {
        setSavingPrivacy(false);
      }
    },
    [chatId, isPrivate, savingPrivacy],
  );

  const handlePrivacyChange = useCallback(
    (checked: boolean) => {
      void handlePrivacyToggle(Boolean(checked));
    },
    [handlePrivacyToggle],
  );

  const {
    input,
    setInput,
    msgList,
    loading,
    error,
    streamingMsg,
    bottomRef,
    inputRef,
    copiedId,
    handleCopy,
    handleSend,
    image,
    setImage,
  } = useChatStream({
    streamUrl: `/patients/chats/${chatId}/messages_stream`,
    initialMessages: chat.messages,
    onStreamComplete: async () => {
      decreaseCount();

      const refreshChatMeta = async () => {
        const details = await fetchChatDetails();
        if (details?.title) {
          setTitle(details.title);
        }
        if (typeof details?.private === 'boolean') {
          setIsPrivate(details.private);
        }
      };

      await refreshChatMeta();
    },
  });

  const safetyBlockedBanner = useMemo(() => {
    if (!isSafetyBlocked) {
      return null;
    }

    const message = chat.content_hidden
      ? 'Questa chat è stata bloccata per motivi di sicurezza e i contenuti sono temporaneamente nascosti. Il tuo terapeuta ti contatterà a breve.'
      : 'Questa chat è stata bloccata per motivi di sicurezza. Il tuo terapeuta è stato notificato.';

    return (
      <Card variant="flat" semantic="destructive" className="gap-4 px-6 py-5">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-destructive/10">
            <ShieldAlert className="h-5 w-5 text-destructive" aria-hidden="true" />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <Heading level={4}>Chat bloccata</Heading>
            <p className="text-sm text-foreground">{message}</p>
          </div>
        </div>
      </Card>
    );
  }, [isSafetyBlocked, chat.content_hidden]);

  const expiredBanner = useMemo(() => {
    if (!isChatExpired) {
      return null;
    }

    return (
      <Card variant="flat" semantic="destructive" className="gap-4 px-4 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <Heading level={4}>Chat scaduta</Heading>
            <p>Questa chat è ora in sola lettura. Per continuare, apri una nuova chat.</p>
          </div>
          <Button asChild className="w-full sm:w-auto">
            <Link href="/patients/chats/new">Apri nuova chat</Link>
          </Button>
        </div>
      </Card>
    );
  }, [isChatExpired]);

  const infoBanner = useMemo(() => {
    const privacyBanner = !isPrivate ? (
      <Card variant="flat" semantic="destructive" className="gap-2 px-3 py-2">
        <p className="text-destructive">
          Lo psicologo potrà leggere questi messaggi oltre al riassunto.
        </p>
      </Card>
    ) : null;

    const expiresTodayBanner = expiresToday ? (
      <Card variant="flat" semantic="warning" className="gap-2 px-4 py-3">
        <p>Hai ancora oggi per mandare nuovi messaggi in questa chat.</p>
      </Card>
    ) : null;

    const availability = isChatExpired ? null : (
      <ChatAvailabilityBanner
        remaining={remainingMessages}
        warningThreshold={limitStatus.warningThreshold}
      />
    );

    if (
      !privacyBanner &&
      !availability &&
      !expiresTodayBanner &&
      !expiredBanner &&
      !safetyBlockedBanner
    ) {
      return null;
    }

    return (
      <div className="flex flex-col gap-2">
        {safetyBlockedBanner}
        {expiredBanner}
        {privacyBanner}
        {expiresTodayBanner}
        {availability}
      </div>
    );
  }, [
    isPrivate,
    isChatExpired,
    expiresToday,
    remainingMessages,
    limitStatus.warningThreshold,
    expiredBanner,
    safetyBlockedBanner,
  ]);

  // Se la chat è bloccata E i contenuti sono nascosti, mostra solo il banner
  if (isSafetyBlocked && chat.content_hidden) {
    return (
      <Layout user={{ name: 'Cliente' }} scrollMode="content">
        <div className="flex h-full flex-col overflow-hidden p-6">
          <div className="mb-6">
            <Heading level={2}>{title}</Heading>
          </div>
          {safetyBlockedBanner}
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={{ name: 'Cliente' }} scrollMode="content">
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <ChatInterface
          title={title}
          headerActions={
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-3">
                {!isPrivate && (
                  <Badge variant="destructive" className="whitespace-nowrap">
                    Visibile allo psicologo
                  </Badge>
                )}
                {savingPrivacy && (
                  <Badge variant="secondary" className="whitespace-nowrap animate-pulse">
                    Salvataggio…
                  </Badge>
                )}
                <div className="flex items-center gap-2">
                  <Label htmlFor="chat-privacy-toggle" className="text-sm text-muted-foreground">
                    Chat privata
                  </Label>
                  <Switch
                    id="chat-privacy-toggle"
                    checked={isPrivate}
                    onCheckedChange={handlePrivacyChange}
                    disabled={savingPrivacy}
                    aria-label="Imposta se la chat resta privata"
                  />
                </div>
              </div>
              {privacyError && <span className="text-xs text-destructive">{privacyError}</span>}
            </div>
          }
          infoBanner={infoBanner}
          input={input}
          setInput={setInput}
          msgList={msgList}
          loading={loading}
          error={error}
          streamingMsg={streamingMsg}
          bottomRef={bottomRef}
          inputRef={inputRef}
          copiedId={copiedId}
          handleCopy={handleCopy}
          handleSend={handleSend}
          readOnly={isChatBlocked || isChatExpired || isSafetyBlocked}
          image={image}
          setImage={setImage}
          heightClass="flex-1"
        />
      </div>
    </Layout>
  );
}
